import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { PrivateRoute } from './components/auth/PrivateRoute';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { initSupabaseDebug } from './lib/supabaseDebug';
import LoadingSpinner from './components/LoadingSpinner';
import { usePageView } from './hooks/usePageView';
import { ToastProvider } from './components/ui/feedback/Toast';
import AnalyticsProvider from './components/AnalyticsProvider';

import AdminBlogs from './pages/admin/AdminBlogs';
import AdminEvents from './pages/admin/AdminEvents';
import AdminDataSync from './pages/admin/AdminDataSync';
import DatabaseSetup from './pages/admin/DatabaseSetup';
// Lazy load NerdMapPage to avoid eager loading map dependencies on non-map routes
const NerdMapPage = lazy(() => import('./pages/NerdMapPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const HomePage = lazy(() => import('./pages/HomePage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const ToolkitPage = lazy(() => import('./pages/ToolkitPage'));
const ToolDetailPage = lazy(() => import('./pages/ToolDetailPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const MerchPage = lazy(() => import('./pages/MerchPage'));
const CheatSheetsPage = lazy(() => import('./pages/CheatSheetsPage'));
const CheatSheetReader = lazy(() => import('./pages/CheatSheetReader'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const SubscribePage = lazy(() => import('./pages/SubscribePage'));
const StemDirectoryPage = lazy(() => import('./pages/StemDirectoryPage'));
const StemSubmitPage = lazy(() => import('./pages/StemSubmitPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));
const SpaceEventsTracker = lazy(() => import('./pages/SpaceEventsTracker'));
const RocketCalculator = lazy(() => import('./pages/RocketCalculator'));
// Admin pages - New Components
const NewsletterAnalytics = lazy(() => import('./pages/admin/NewsletterAnalytics'));
const NewsletterComposer = lazy(() => import('./pages/admin/NewsletterComposer'));
const NewslettersList = lazy(() => import('./pages/admin/NewslettersList'));
const NewsletterDetails = lazy(() => import('./pages/admin/NewsletterDetails'));
// Auth pages
const SignInPage = lazy(() => import('./pages/auth/SignInPage'));
const SignUpPage = lazy(() => import('./pages/auth/SignUpPage'));
// Space feature pages
const SpaceEventsPage = lazy(() => import('./pages/space/events/EventsPage'));
const CalendarEventsPage = lazy(() => import('./pages/EventsPage'));
const EventsMapPage = lazy(() => import('./pages/EventsMapPage'));
const MapPage = lazy(() => import('./pages/MapPage'));
const TimelinePage = lazy(() => import('./pages/TimelinePage'));
const NewsPage = lazy(() => import('./pages/space/news/NewsPage'));
const ObservationsPage = lazy(
  () => import('./pages/space/observations/ObservationsPage'),
);
const BadgesPage = lazy(() => import('./pages/space/badges/BadgesPage'));
const NASAPage = lazy(() => import('./pages/space/nasa/NASAPage'));
const ArtGalleryPage = lazy(
  () => import('./pages/space/art-gallery/ArtGalleryPage'),
);
const StarsPage = lazy(() => import('./pages/space/StarsPage'));
const SpaceWeatherPage = lazy(
  () => import('./pages/space/weather/SpaceWeatherPage'),
);
const NightSkyPage = lazy(() => import('./pages/space/NightSkyPage'));
const SpaceHubPage = lazy(() => import('./pages/space/SpaceHubPage'));
const TodayPage = lazy(() => import('./pages/space/TodayPage'));
const ISSPage = lazy(() => import('./pages/space/ISSPage'));
const MissionsGalleryPage = lazy(() => import('./pages/space/MissionsGalleryPage'));
const HistoryGalleryPage = lazy(() => import('./pages/space/HistoryGalleryPage'));

// Admin components
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
const AdminDrafts = lazy(() => import('./pages/admin/drafts'));
const AffiliateLinksAdmin = lazy(() => import('./pages/admin/affiliate-links'));
const AdminNASAKeys = lazy(() => import('./pages/admin/AdminNASAKeys'));
const AdminStem = lazy(() => import('./pages/admin/AdminStem'));

function App() {
  useEffect(() => {
    // Initialize Supabase debug helper in development or if debug param is present
    // Skip if we're accessing Mars rover functionality to avoid connection conflicts
    if (
      (import.meta.env.DEV ||
      new URLSearchParams(window.location.search).has('debug')) &&
      !window.location.pathname.includes('/space/nasa') // Skip debug for NASA pages
    ) {
      console.log('🐛 Initializing Supabase debug monitoring...');
      initSupabaseDebug();
    }
  }, []);

  return (
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ThemeProvider>
              <ToastProvider>
                <Helmet>
                  <title>
                    Rocket City Nerd - Your Gateway to Engineering Excellence
                  </title>
                  <meta
                    name="description"
                    content="Explore the world of engineering, model rocketry, and STEM projects with Rocket City Nerd. Tutorials, blog posts, tools, and community."
                  />
                  <meta property="og:type" content="website" />
                  <meta property="og:site_name" content="Rocket City Nerd" />
                  <link rel="icon" href="/favicon.png" />
                  {/* RSS feeds */}
                  <link rel="alternate" type="application/rss+xml" title="Rocket City Nerd — All Posts" href="/feed.xml" />
                  <link rel="alternate" type="application/rss+xml" title="Rocket City Nerd — Upcoming Launches" href="/rss/launches.xml" />
                  <link rel="alternate" type="application/rss+xml" title="Rocket City Nerd — EPIC Earth" href="/rss/epic.xml" />
                  <link rel="alternate" type="application/rss+xml" title="Rocket City Nerd — Space Weather" href="/rss/spaceweather.xml" />
                  <link rel="alternate" type="application/rss+xml" title="Rocket City Nerd — Near‑Earth Objects" href="/rss/neos.xml" />
                  <meta name="twitter:card" content="summary_large_image" />
                </Helmet>
                <Router 
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
                  <AnalyticsProvider>
                    <InnerApp />
                  </AnalyticsProvider>
                </Router>
              </ToastProvider>
            </ThemeProvider>
          </AuthProvider>
        </QueryClientProvider>
      </HelmetProvider>
  );
}

function InnerApp() {
  usePageView();

  return (
    <Layout>
      <ErrorBoundary>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Auth Routes */}
            <Route path="/signin" element={<SignInPage />} />
            <Route path="/signup" element={<SignUpPage />} />

            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/toolkit" element={<ToolkitPage />} />
            <Route path="/tool/:slug" element={<ToolDetailPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/subscribe" element={<SubscribePage />} />
            <Route path="/merch" element={<MerchPage />} />
            <Route path="/cheat-sheets" element={<CheatSheetsPage />} />
            <Route path="/cheat-sheets/:slug" element={<CheatSheetReader />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/stem" element={<StemDirectoryPage />} />
            <Route path="/stem/submit" element={<StemSubmitPage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/space-events" element={<SpaceEventsTracker />} />
            <Route path="/rocket-calculator" element={<RocketCalculator />} />

            {/* Space feature routes */}
            <Route path="/space" element={<SpaceHubPage />} />
            <Route path="/space/iss" element={<ISSPage />} />
            <Route path="/space/events" element={<SpaceEventsPage />} />
            {/* Aliases to match sitemap and external links */}
            <Route path="/space/events/tracker" element={<SpaceEventsTracker />} />
            <Route path="/events" element={<CalendarEventsPage />} />
            <Route path="/events/map" element={<EventsMapPage />} />
            <Route path="/map" element={<MapPage />} />
            <Route path="/nerd-map" element={<NerdMapPage />} />
            <Route path="/timeline" element={<TimelinePage />} />
            <Route path="/space/news" element={<NewsPage />} />
            <Route
              path="/space/observations"
              element={
                <PrivateRoute>
                  <ObservationsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/space/badges"
              element={
                <PrivateRoute>
                  <BadgesPage />
                </PrivateRoute>
              }
            />
            <Route path="/space/nasa" element={<NASAPage />} />
            <Route path="/space/weather" element={<SpaceWeatherPage />} />
            <Route path="/space/nightsky" element={<NightSkyPage />} />
            {/* Alias for hyphenated variant listed in sitemap */}
            <Route path="/space/night-sky" element={<NightSkyPage />} />
            <Route path="/space/art" element={<ArtGalleryPage />} />
            <Route path="/space/stars" element={<StarsPage />} />
            <Route path="/space/missions" element={<MissionsGalleryPage />} />
            <Route path="/space/history" element={<HistoryGalleryPage />} />
            {/* APOD Today route */}
            <Route path="/space/today" element={<TodayPage />} />

            {/* Protected Admin Routes */}
            <Route
              path="/admin"
              element={
                <PrivateRoute requireAdmin>
                  <AdminLayout />
                </PrivateRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="blogs" element={<AdminBlogs />} />
              <Route path="events" element={<AdminEvents />} />
              <Route path="data-sync" element={<AdminDataSync />} />
              <Route path="drafts" element={<AdminDrafts />} />
              <Route path="stem" element={<AdminStem />} />
              <Route path="affiliate-links" element={<AffiliateLinksAdmin />} />
              <Route path="nasa-keys" element={<AdminNASAKeys />} />
              <Route path="newsletters" element={<NewslettersList />} />
              <Route path="newsletter-analytics" element={<NewsletterAnalytics />} />
              <Route path="newsletter-composer" element={<NewsletterComposer />} />
              <Route path="newsletter-details" element={<NewsletterDetails />} />
            </Route>

            {/* 404 Route */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </Layout>
  );
}

export default App;
